import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { createProduct, fetchProduct, updateProduct } from '../services/productsService';
import { fetchBirimler } from '../services/birimService';
import './IcerikExcelImportModal.css';

/** 0-based column index → A, B, …, Z, AA, … */
const colIndexToLetters = (index) => {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/** Excel sütun harfi → 0 tabanlı indeks; geçersiz/boş → null (max XFD) */
const lettersToColIndex = (letters) => {
  if (!letters || typeof letters !== 'string') return null;
  const s = letters.trim().toUpperCase().replace(/[^A-Z]/g, '');
  if (!s) return null;
  let n = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i) - 64;
    if (c < 1 || c > 26) return null;
    n = n * 26 + c;
  }
  if (n < 1 || n > 16384) return null;
  return n - 1;
};

/** @typedef {'text'|'price'|'bool'|'birim'} FieldKind — alt modalda varsayılan girişi */
const FIELD_ROWS = {
  urun: [
    { key: 'icerikAdi', label: 'Ürün adı', zorunlu: true, fieldKind: 'text' },
    { key: 'icerikKodu', label: 'Ürün kodu', zorunlu: true, fieldKind: 'text' },
    { key: 'aciklama', label: 'Açıklama', zorunlu: false, fieldKind: 'text' },
    { key: 'malKodu', label: 'Mal kodu', zorunlu: false, fieldKind: 'text' },
    { key: 'modelKodu', label: 'Model kodu', zorunlu: false, fieldKind: 'text' },
    { key: 'urunAdi2', label: 'Ürün adı 2', zorunlu: false, fieldKind: 'text' },
    { key: 'kurulumGarantiSuresiAy', label: 'Kurulum garanti süresi (ay)', zorunlu: false, fieldKind: 'text' },
  ],
  parca: [
    { key: 'icerikAdi', label: 'Parça adı', zorunlu: true, fieldKind: 'text' },
    { key: 'icerikKodu', label: 'Parça kodu', zorunlu: true, fieldKind: 'text' },
    { key: 'aciklama', label: 'Açıklama', zorunlu: false, fieldKind: 'text' },
    { key: 'fiyat', label: 'Brüt fiyat', zorunlu: false, fieldKind: 'price' },
    {
      key: 'degisimParcaGarantisi',
      label: 'Değişim parça garantisi (Evet/Hayır)',
      zorunlu: false,
      fieldKind: 'bool',
    },
    {
      key: 'birimId',
      label: 'Birim (Excel’de ad, kod veya ID; yoksa varsayılan)',
      zorunlu: false,
      fieldKind: 'birim',
    },
  ],
  islem: [
    { key: 'icerikAdi', label: 'İşlem adı', zorunlu: true, fieldKind: 'text' },
    { key: 'icerikKodu', label: 'İşlem kodu', zorunlu: true, fieldKind: 'text' },
    { key: 'aciklama', label: 'Açıklama', zorunlu: false, fieldKind: 'text' },
    { key: 'fiyat', label: 'Brüt fiyat', zorunlu: false, fieldKind: 'price' },
  ],
  ariza: [
    { key: 'icerikAdi', label: 'Arıza adı', zorunlu: true, fieldKind: 'text' },
    { key: 'icerikKodu', label: 'Arıza kodu', zorunlu: true, fieldKind: 'text' },
    { key: 'aciklama', label: 'Açıklama', zorunlu: false, fieldKind: 'text' },
  ],
};

/** Parça Excel: tek tıkla standart sütun / varsayılan eşlemesi */
const PARCA_EXCEL_DEFAULT_MAPPINGS = {
  icerikKodu: { column: 'A', default: '' },
  icerikAdi: { column: 'B', default: '' },
  aciklama: { column: '', default: '' },
  fiyat: { column: '', default: '0' },
  degisimParcaGarantisi: { column: '', default: 'false' },
  birimId: { column: 'E', default: '' },
};

/** Parça Excel: veri başlangıç satırı (1 tabanlı) */
const PARCA_EXCEL_DEFAULT_DATA_START_ROW = 2;

const cellToString = (v) => {
  if (v === undefined || v === null) return '';
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (Number.isInteger(v)) return String(v);
    const t = String(v).replace(',', '.');
    return t;
  }
  return String(v);
};

/** @param {{ column?: string, default?: string }} mapping */
const resolveMappedValue = (row, mapping) => {
  const m = mapping || { column: '', default: '' };
  const colLetters = (m.column || '').trim();
  const def = m.default !== undefined && m.default !== null ? String(m.default) : '';
  const idx = lettersToColIndex(colLetters);
  if (idx !== null && idx >= 0 && Array.isArray(row)) {
    const v = row[idx];
    const s = cellToString(v);
    if (String(s).trim() !== '') return s;
    return def;
  }
  return def;
};

const parseListeFiyat = (raw) => {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim().replace(/\s/g, '').replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  if (Number.isNaN(n) || n < 0) return Number.NaN;
  return n;
};

const parseKurulumAy = (raw) => {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n) || n < 1 || n > 120) return Number.NaN;
  return n;
};

const parseBoolTr = (raw) => {
  const t = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (t === '') return null;
  if (['evet', 'e', '1', 'true', 'yes', 'var'].includes(t)) return true;
  if (['hayır', 'hayir', 'h', '0', 'false', 'no', 'yok'].includes(t)) return false;
  return undefined;
};

/** Excel hücresi → normalize edilmiş birim *adı* (küçük harf, tr-TR); yazım hataları için kanon eşlemesi */
const normalizeBirimCell = (s) =>
  String(s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('tr-TR');

/**
 * Hücrede kod değil görünen ad (Kilogram, Metre, Kilgoram vb.) geldiğinde kanon ada çevir.
 * Anahtarlar normalizeBirimCell çıktısı ile aynı formatta olmalı.
 */
const EXCEL_BIRIM_AD_CANON = {
  kilgoram: 'kilogram',
  kilog: 'kilogram',
  'kilo gram': 'kilogram',
  kilogram: 'kilogram',
  kg: 'kilogram',
  g: 'gram',
  gr: 'gram',
  gram: 'gram',
  metre: 'metre',
  meter: 'metre',
  metrekare: 'metrekare',
  m2: 'metrekare',
  'm²': 'metrekare',
  litre: 'litre',
  lt: 'litre',
  adet: 'adet',
  tane: 'adet',
  piece: 'adet',
  pcs: 'adet',
};

/**
 * Excel / varsayılan metninden birim ObjectId (API `birimId`).
 * @param {string} text
 * @param {{ id?: string, _id?: string, kod?: string, ad?: string, aktif?: boolean }[]} birimler
 * @returns {{ birimId?: string, error?: string }} Boş metin → {} (payload’da birimId yok; istemci önce liste sırasındaki ilk birimi doldurur)
 */
const resolveBirimIdFromMappedText = (text, birimler) => {
  const raw = String(text ?? '').trim();
  if (!raw) return {};
  const list = Array.isArray(birimler) ? birimler : [];

  const byId = list.find((b) => String(b.id || b._id || '') === raw);
  if (byId && byId.aktif !== false) return { birimId: String(byId.id || byId._id) };

  const up = raw.replace(/\s/g, '').toUpperCase();
  const byKod = list.find((b) => String(b.kod || '').replace(/\s/g, '').toUpperCase() === up);
  if (byKod && byKod.aktif !== false) return { birimId: String(byKod.id || byKod._id) };

  const key = normalizeBirimCell(raw);
  const byKodNorm = list.find((b) => b.aktif !== false && normalizeBirimCell(b.kod) === key);
  if (byKodNorm) return { birimId: String(byKodNorm.id || byKodNorm._id) };

  const adTarget = EXCEL_BIRIM_AD_CANON[key] || key;
  const byAd = list.find((b) => b.aktif !== false && normalizeBirimCell(b.ad) === adTarget);
  if (byAd) return { birimId: String(byAd.id || byAd._id) };

  return {
    error: `Tanınmayan birim: «${raw}». Birim adı (örn. Kilogram, Metre), kod (KG) veya ID kullanın.`,
  };
};

const isRowWhitespaceOnly = (row) => {
  if (!row || !row.length) return true;
  return row.every((c) => {
    if (c === undefined || c === null) return true;
    const s = String(c).trim();
    return s === '';
  });
};

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   contentType: 'urun'|'parca'|'islem'|'ariza',
 *   kategoriId: string,
 *   categoryName: string,
 *   onImported: () => void,
 *   importMode?: 'kategori'|'urun',
 *   urunIcerikId?: string,
 *   urunLabel?: string,
 * }} props
 */
const IcerikExcelImportModal = ({
  isOpen,
  onClose,
  contentType,
  kategoriId,
  categoryName,
  onImported,
  importMode = 'kategori',
  urunIcerikId = '',
  urunLabel = '',
}) => {
  const fileInputRef = useRef(null);
  const [fileLabel, setFileLabel] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [allRows, setAllRows] = useState([]);
  /** Excel’de verinin başladığı satır (1 tabanlı); üstteki açıklama satırlarını atlamak için */
  const [dataStartRow, setDataStartRow] = useState('1');
  /** Her alan: Excel sütun harfi (A, B, …) ve/veya varsayılan; ikisi doluysa önce sütundan okunur, boşsa varsayılan */
  const [columnMappings, setColumnMappings] = useState({});
  /** Alan eşleme alt modalı: null | taslak */
  const [mappingEdit, setMappingEdit] = useState(null);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  /** Parça Excel birimi: sütun/varsayılan çözümü için (ad, kod veya id eşlemesi) */
  const [birimler, setBirimler] = useState([]);

  /** API sırası: backend `sira`, sonra `ad` */
  const firstBirimId = useMemo(() => {
    const b = birimler[0];
    return b ? String(b.id || b._id) : '';
  }, [birimler]);

  const effectiveBirimDefaultId = useCallback(
    (val) => {
      const t = String(val ?? '').trim();
      if (t) return t;
      return firstBirimId;
    },
    [firstBirimId]
  );

  const fieldList = FIELD_ROWS[contentType] || FIELD_ROWS.urun;

  const sheetNames = useMemo(() => (workbook && workbook.SheetNames ? workbook.SheetNames : []), [workbook]);

  /** Ham sayfanın ilk 10 satırı: sütun sayısı sağdan boş sütunları kırpar */
  const excelRawPreview = useMemo(() => {
    const maxRows = 10;
    const slice = allRows.slice(0, maxRows);
    if (!slice.length) return { numCols: 0, rows: [] };
    let maxLen = 0;
    slice.forEach((r) => {
      if (Array.isArray(r)) maxLen = Math.max(maxLen, r.length);
    });
    let c = maxLen;
    while (c > 0) {
      const ci = c - 1;
      const any = slice.some((r) => {
        if (!Array.isArray(r)) return false;
        const v = r[ci];
        return v !== undefined && v !== null && String(v).trim() !== '';
      });
      if (any) break;
      c -= 1;
    }
    return { numCols: c, rows: slice };
  }, [allRows]);

  /** Geçerli Excel veri başlangıç satırı (1…N), geçersiz girişte null */
  const parsedDataStartRow = useMemo(() => {
    const n = parseInt(String(dataStartRow).trim(), 10);
    if (!Number.isFinite(n) || n < 1) return null;
    return n;
  }, [dataStartRow]);

  const dataRows = useMemo(() => {
    if (!allRows.length) return [];
    if (parsedDataStartRow === null) return [];
    const sliceStart = parsedDataStartRow - 1;
    if (sliceStart >= allRows.length) return [];
    const rows = allRows.slice(sliceStart);
    return rows.filter((r) => !isRowWhitespaceOnly(r));
  }, [allRows, parsedDataStartRow]);

  const getExcelRowNumber = useCallback(
    (dataRowIndex) => {
      if (parsedDataStartRow === null) return dataRowIndex + 1;
      return parsedDataStartRow + dataRowIndex;
    },
    [parsedDataStartRow],
  );

  const resetWizard = useCallback(() => {
    setFileLabel('');
    setWorkbook(null);
    setSelectedSheet('');
    setAllRows([]);
    setDataStartRow('1');
    const init = {};
    fieldList.forEach((f) => {
      init[f.key] = { column: '', default: '' };
    });
    setColumnMappings(init);
    setMappingEdit(null);
    setParseError('');
    setLastResult(null);
  }, [fieldList]);

  useEffect(() => {
    if (!isOpen) {
      resetWizard();
      return;
    }
    const init = {};
    fieldList.forEach((f) => {
      init[f.key] = { column: '', default: '' };
    });
    setColumnMappings(init);
    setDataStartRow('1');
    setMappingEdit(null);
    setLastResult(null);
    setParseError('');
  }, [isOpen, contentType, fieldList, resetWizard]);

  useEffect(() => {
    if (!isOpen || contentType !== 'parca') {
      setBirimler([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchBirimler({ aktif: true });
        const arr = r?.veri?.birimler || [];
        if (!cancelled) setBirimler(Array.isArray(arr) ? arr : []);
      } catch {
        if (!cancelled) setBirimler([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, contentType]);

  useEffect(() => {
    if (!workbook || !selectedSheet) return;
    try {
      const ws = workbook.Sheets[selectedSheet];
      if (!ws) return;
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
      setAllRows(Array.isArray(rows) ? rows : []);
      setParseError('');
    } catch (e) {
      setParseError(e?.message || 'Sayfa okunamadı');
    }
  }, [workbook, selectedSheet]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setFileLabel(file.name);
    setParseError('');
    setLastResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        setWorkbook(wb);
        const name0 = wb.SheetNames && wb.SheetNames[0] ? wb.SheetNames[0] : '';
        setSelectedSheet(name0);
      } catch (err) {
        setWorkbook(null);
        setSelectedSheet('');
        setAllRows([]);
        setParseError(err?.message || 'Excel dosyası okunamadı');
      }
    };
    reader.onerror = () => {
      setParseError('Dosya okunamadı');
    };
    reader.readAsArrayBuffer(file);
  };

  const openMappingEdit = (f) => {
    const cur = columnMappings[f.key] || { column: '', default: '' };
    let def = cur.default !== undefined && cur.default !== null ? String(cur.default) : '';
    if ((f.fieldKind || 'text') === 'birim') {
      def = effectiveBirimDefaultId(def);
    }
    setMappingEdit({
      key: f.key,
      label: f.label,
      fieldKind: f.fieldKind || 'text',
      column: cur.column || '',
      default: def,
    });
  };

  const saveMappingEdit = () => {
    if (!mappingEdit) return;
    let defOut = mappingEdit.default;
    if (mappingEdit.fieldKind === 'birim') {
      defOut = effectiveBirimDefaultId(mappingEdit.default);
    }
    setColumnMappings((prev) => ({
      ...prev,
      [mappingEdit.key]: {
        column: mappingEdit.column,
        default: defOut,
      },
    }));
    setMappingEdit(null);
  };

  const applyParcaDefaultMappings = useCallback(() => {
    if (contentType !== 'parca') return;
    setColumnMappings((prev) => ({
      ...prev,
      ...PARCA_EXCEL_DEFAULT_MAPPINGS,
    }));
    setDataStartRow(String(PARCA_EXCEL_DEFAULT_DATA_START_ROW));
    setMappingEdit(null);
  }, [contentType]);

  const getCardSummary = (f) => {
    const map = columnMappings[f.key] || { column: '', default: '' };
    const col = (map.column || '').trim();
    const def = (map.default || '').trim();
    const colOk = col && lettersToColIndex(col) !== null;
    const kind = f.fieldKind || 'text';

    if (kind === 'birim') {
      const rawDef = def;
      const eff = effectiveBirimDefaultId(rawDef);
      const labelForId = (id) => {
        if (!id) return 'Birim bulunamadı';
        const b = (birimler || []).find((x) => String(x.id || x._id) === id);
        return b ? `${b.ad} (${b.kod})` : 'Birim bulunamadı';
      };
      if (col && !colOk) return 'Geçersiz sütun harfi';
      if (colOk) {
        if (rawDef) {
          const b = (birimler || []).find((x) => String(x.id || x._id) === rawDef);
          const part = b ? `Varsayılan: ${b.ad} (${b.kod})` : 'Varsayılan: (geçersiz)';
          return `Sütun ${col.toUpperCase()} · ${part}`;
        }
        return `Sütun ${col.toUpperCase()} · boş hücre → ${labelForId(eff)}`;
      }
      if (rawDef) {
        const b = (birimler || []).find((x) => String(x.id || x._id) === rawDef);
        return b ? `Varsayılan: ${b.ad} (${b.kod})` : 'Varsayılan: (geçersiz)';
      }
      return labelForId(eff);
    }

    const defLabel = () => {
      if (kind === 'bool') {
        if (def === 'true') return 'Varsayılan: Evet';
        if (def === 'false') return 'Varsayılan: Hayır';
        return '';
      }
      if (def) return `Varsayılan: ${def}`;
      return '';
    };

    const dl = defLabel();
    if (colOk && dl) return `Sütun ${col.toUpperCase()} · ${dl}`;
    if (colOk) return `Sütun ${col.toUpperCase()}`;
    if (dl) return dl;
    if (col) return 'Geçersiz sütun harfi';
    return 'Eşleme yok';
  };

  const validateMappingsBeforeImport = () => {
    if (parsedDataStartRow === null) {
      return 'Veri başlangıç satırı 1 veya daha büyük bir tam sayı olmalıdır.';
    }
    if (parsedDataStartRow > allRows.length) {
      return `Veri başlangıç satırı (${parsedDataStartRow}) sayfadaki satır sayısından büyük olamaz.`;
    }
    const m = columnMappings;
    const hasSource = (key) => {
      const map = m[key];
      if (!map) return false;
      const col = (map.column || '').trim();
      const def = (map.default || '').trim();
      if (col && lettersToColIndex(col) !== null) return true;
      if (def !== '') return true;
      return false;
    };
    if (!hasSource('icerikAdi')) {
      return 'Ad için geçerli sütun harfi veya varsayılan değer girin.';
    }
    if (!hasSource('icerikKodu')) {
      return 'Kod için geçerli sütun harfi veya varsayılan değer girin.';
    }
    if (contentType === 'parca' || contentType === 'islem') {
      if (!hasSource('fiyat')) {
        return 'Brüt fiyat için geçerli sütun harfi veya sayısal varsayılan değer girin.';
      }
    }
    if (importMode === 'urun' && contentType === 'parca') {
      if (!urunIcerikId || String(urunIcerikId).trim() === '') {
        return 'Ürün bağlamı eksik. Modalı kapatıp bir ürün seçerek tekrar deneyin.';
      }
    }
    return null;
  };

  const buildPayloadForRow = (row) => {
    const m = columnMappings;
    const icerikAdi = resolveMappedValue(row, m.icerikAdi);
    const icerikKodu = resolveMappedValue(row, m.icerikKodu);
    const aciklama = resolveMappedValue(row, m.aciklama) || '';

    const payload = {
      icerikTipi: contentType,
      icerikAdi,
      icerikKodu,
      aciklama: aciklama || null,
    };

    if (importMode === 'urun' && contentType === 'parca' && urunIcerikId) {
      payload.kategoriId = null;
      payload.bagliUrunIcerikIdleri = [String(urunIcerikId).trim()];
    } else {
      payload.kategoriId = kategoriId;
    }

    if (contentType === 'urun') {
      if (m.malKodu) payload.malKodu = resolveMappedValue(row, m.malKodu).trim();
      if (m.modelKodu) payload.modelKodu = resolveMappedValue(row, m.modelKodu).trim();
      if (m.urunAdi2) {
        const u2 = resolveMappedValue(row, m.urunAdi2).trim();
        payload.urunAdi2 = u2 === '' ? null : u2;
      }
      const kgRaw = resolveMappedValue(row, m.kurulumGarantiSuresiAy);
      if (String(kgRaw).trim() !== '') {
        const kg = parseKurulumAy(kgRaw);
        if (kg === null || Number.isNaN(kg)) {
          return { error: 'Kurulum garanti süresi 1–120 ay arasında olmalıdır.' };
        }
        payload.kurulumGarantiSuresiAy = kg;
      }
    }

    if (contentType === 'parca' || contentType === 'islem') {
      const rawF = resolveMappedValue(row, m.fiyat);
      const fiyatVal = parseListeFiyat(rawF);
      if (fiyatVal === null || Number.isNaN(fiyatVal)) {
        return { error: 'Geçerli brüt fiyat yok (sütun veya varsayılan).' };
      }
      payload.fiyat = fiyatVal;
    }

    if (contentType === 'parca') {
      const rawD = resolveMappedValue(row, m.degisimParcaGarantisi);
      if (String(rawD).trim() !== '') {
        const b = parseBoolTr(rawD);
        if (b === undefined) {
          return { error: 'Değişim parça garantisi Evet/Hayır veya 1/0 olmalıdır.' };
        }
        payload.degisimParcaGarantisi = b === true;
      } else {
        payload.degisimParcaGarantisi = false;
      }

      const birimMap = m.birimId || { column: '', default: '' };
      const birimRaw = resolveMappedValue(row, {
        ...birimMap,
        default: effectiveBirimDefaultId(birimMap.default),
      });
      const birimResolved = resolveBirimIdFromMappedText(birimRaw, birimler);
      if (birimResolved.error) return { error: birimResolved.error };
      if (birimResolved.birimId) payload.birimId = birimResolved.birimId;
    }

    return { payload };
  };

  const validateRowLocal = (row, excelRowNum) => {
    const adi = resolveMappedValue(row, columnMappings.icerikAdi);
    const kod = resolveMappedValue(row, columnMappings.icerikKodu);
    if (!adi || String(adi).trim() === '') {
      return `Satır ${excelRowNum}: Ad boş olamaz.`;
    }
    if (String(adi).length > 200) {
      return `Satır ${excelRowNum}: Ad en fazla 200 karakter olabilir.`;
    }
    if (!kod || String(kod).trim() === '') {
      return `Satır ${excelRowNum}: Kod boş olamaz.`;
    }
    if (/\s/.test(String(kod))) {
      return `Satır ${excelRowNum}: Kod boşluk içeremez.`;
    }
    if (String(kod).length > 50) {
      return `Satır ${excelRowNum}: Kod en fazla 50 karakter olabilir.`;
    }
    const aciklama = resolveMappedValue(row, columnMappings.aciklama);
    if (aciklama && String(aciklama).length > 2000) {
      return `Satır ${excelRowNum}: Açıklama en fazla 2000 karakter olabilir.`;
    }
    const built = buildPayloadForRow(row);
    if (built.error) {
      return `Satır ${excelRowNum}: ${built.error}`;
    }
    return null;
  };

  const runImport = async () => {
    setLastResult(null);
    const msg = validateMappingsBeforeImport();
    if (msg) {
      setParseError(msg);
      return;
    }
    if (!dataRows.length) {
      setParseError('İçe aktarılacak veri satırı yok.');
      return;
    }

    setImporting(true);
    setParseError('');

    const errors = [];
    let success = 0;

    for (let i = 0; i < dataRows.length; i += 1) {
      const row = dataRows[i];
      const excelRowNum = getExcelRowNumber(i);

      const localErr = validateRowLocal(row, excelRowNum);
      if (localErr) {
        errors.push({ row: excelRowNum, message: localErr });
        continue;
      }

      const built = buildPayloadForRow(row);
      if (built.error) {
        errors.push({ row: excelRowNum, message: `Satır ${excelRowNum}: ${built.error}` });
        continue;
      }

      try {
        const res = await createProduct(built.payload);
        if (res && res.basarili) {
          success += 1;
        } else {
          errors.push({
            row: excelRowNum,
            message: `Satır ${excelRowNum}: ${res?.mesaj || 'Kayıt oluşturulamadı'}`,
          });
        }
      } catch (err) {
        const is409 = err?.status === 409;
        const existingId = err?.data?.mevcutIcerikId;
        if (
          importMode === 'urun' &&
          contentType === 'parca' &&
          is409 &&
          existingId &&
          urunIcerikId
        ) {
          try {
            const uid = String(urunIcerikId).trim();
            const got = await fetchProduct(existingId);
            const doc = got?.veri?.icerik || got?.veri?.parca || got?.veri;
            const rawBagli = Array.isArray(doc?.bagliUrunIcerikIdleri)
              ? doc.bagliUrunIcerikIdleri
              : [];
            const current = rawBagli.map((x) =>
              x != null && typeof x === 'object' && x._id != null ? String(x._id) : String(x),
            );
            if (current.some((id) => String(id) === uid)) {
              success += 1;
            } else {
              await updateProduct(String(existingId), {
                icerikTipi: 'parca',
                bagliUrunIcerikIdleri: [...current, uid],
              });
              success += 1;
            }
          } catch (mergeErr) {
            errors.push({
              row: excelRowNum,
              message: `Satır ${excelRowNum}: ${mergeErr?.message || 'Mevcut parçaya ürün eklenemedi'}`,
            });
          }
        } else {
          errors.push({
            row: excelRowNum,
            message: `Satır ${excelRowNum}: ${err?.message || 'İstek başarısız'}`,
          });
        }
      }
    }

    setLastResult({ success, errors, total: dataRows.length });
    setImporting(false);
    if (success > 0 && typeof onImported === 'function') {
      onImported();
    }
  };

  if (!isOpen) return null;

  const tipTitle =
    contentType === 'urun'
      ? 'Ürün'
      : contentType === 'parca'
        ? 'Parça'
        : contentType === 'islem'
          ? 'İşlem'
          : 'Arıza';

  return (
    <>
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="icerik-excel-import-title"
      onClick={onClose}
    >
      <div
        className="modal-content content-item-form-modal icerik-excel-import-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="icerik-excel-import-title">Excel ile {tipTitle} aktar</h2>
          {importMode === 'urun' && urunLabel ? (
            <p className="icerik-excel-import__subtitle">Ürün: {urunLabel}</p>
          ) : categoryName ? (
            <p className="icerik-excel-import__subtitle">Kategori: {categoryName}</p>
          ) : null}
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="content-item-form-modal__scroll icerik-excel-import__body">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="icerik-excel-import__file-input"
            onChange={handleFileChange}
          />

          <div className="form-group">
            <button
              type="button"
              className="secondary-btn"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <i className="fas fa-file-upload" /> Excel seç (.xls / .xlsx)
            </button>
            {fileLabel ? <span className="icerik-excel-import__file-name">{fileLabel}</span> : null}
          </div>

          {sheetNames.length > 1 ? (
            <div className="form-group">
              <label htmlFor="icerik-excel-sheet">Sayfa</label>
              <select
                id="icerik-excel-sheet"
                className="form-control"
                value={selectedSheet}
                onChange={(e) => setSelectedSheet(e.target.value)}
              >
                {sheetNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {allRows.length > 0 ? (
            <>
              <div className="form-group">
                <label htmlFor="icerik-excel-data-start">Veri başlangıç satırı (Excel satır no.)</label>
                <input
                  id="icerik-excel-data-start"
                  type="number"
                  min={1}
                  max={Math.max(allRows.length, 1)}
                  className="form-control"
                  value={dataStartRow}
                  onChange={(e) => setDataStartRow(e.target.value)}
                  autoComplete="off"
                />
                <p className="icerik-excel-import__field-hint">
                  Tüm sütunlar (A, B, …) bu satırdan itibaren okunur. Üstte açıklama veya boş satırlar varsa, verinin
                  başladığı satır numarasını girin (örnek: veri 5. satırdan başlıyorsa &quot;5&quot;).
                </p>
              </div>

              <p className="icerik-excel-import__hint">
                Bir alanın üzerine tıklayın; açılan pencerede <strong>Excel sütun harfi</strong> ve gerekiyorsa{' '}
                <strong>varsayılan</strong> girin. İkisi doluysa önce sütun kullanılır, hücre boşsa varsayılan. Parça ve
                işlemde <strong>brüt fiyat</strong> için sütun ve/veya sayısal varsayılan kullanılabilir.
                {contentType === 'parca' ? (
                  <>
                    {' '}
                    Parçada <strong>birim</strong> için de sütun (hücrede <strong>görünen ad</strong> veya{' '}
                    <strong>kod</strong>) ve/veya <strong>Ayarlar → Birim</strong> listesindeki sıraya göre ilk birim
                    kullanılır; hiç birim tanımlı değilse <strong>Birim bulunamadı</strong> (içe aktarmada birim
                    gönderilmez).
                  </>
                ) : null}
              </p>

              {importMode === 'urun' && contentType === 'parca' ? (
                <p className="icerik-excel-import__hint">
                  Parçalar <strong>kategorisiz</strong> oluşturulur ve seçili ürüne bağlanır. Kod sistemde zaten varsa
                  yeni parça eklenmez; yalnızca bu ürün bağlamı eklenir (aynı üründe zaten varsa satır yine başarılı sayılır).
                </p>
              ) : null}

              <div className="icerik-excel-import__cards" role="list">
                {fieldList.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className="icerik-excel-import__field-card"
                    onClick={() => openMappingEdit(f)}
                    role="listitem"
                  >
                    <span className="icerik-excel-import__field-card-title">
                      {f.label}
                      {f.zorunlu ? <span className="icerik-excel-import__req"> *</span> : null}
                    </span>
                    <span className="icerik-excel-import__field-card-meta">{getCardSummary(f)}</span>
                  </button>
                ))}
              </div>

              {parseError ? <div className="error-message" role="alert">{parseError}</div> : null}

              {lastResult ? (
                <div className="icerik-excel-import__result" role="status">
                  <p>
                    <strong>{lastResult.success}</strong> kayıt eklendi,{' '}
                    <strong>{lastResult.errors.length}</strong> satırda hata (toplam {lastResult.total} satır).
                  </p>
                  {lastResult.errors.length > 0 ? (
                    <ul className="icerik-excel-import__error-list">
                      {lastResult.errors.slice(0, 50).map((e, idx) => (
                        <li key={`${e.row}-${idx}`}>{e.message}</li>
                      ))}
                      {lastResult.errors.length > 50 ? (
                        <li>… ve {lastResult.errors.length - 50} hata daha</li>
                      ) : null}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              <div className="icerik-excel-import__raw-preview">
                <h3 className="icerik-excel-import__preview-title">Excel önizlemesi (ilk 10 satır)</h3>
                {excelRawPreview.numCols === 0 ? (
                  <p className="content-accordion-hint">Bu sayfada gösterilecek hücre yok.</p>
                ) : (
                  <div className="icerik-excel-import__raw-scroll">
                    <table className="admins-table icerik-excel-import__raw-table">
                      <thead>
                        <tr>
                          <th className="icerik-excel-import__raw-rownum">#</th>
                          {Array.from({ length: excelRawPreview.numCols }, (_, ci) => (
                            <th key={`ch-${ci}`}>{colIndexToLetters(ci)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {excelRawPreview.rows.map((row, ri) => (
                          <tr key={`raw-${ri}`}>
                            <td className="icerik-excel-import__raw-rownum">{ri + 1}</td>
                            {Array.from({ length: excelRawPreview.numCols }, (_, ci) => (
                              <td key={`c-${ri}-${ci}`}>{cellToString(row ? row[ci] : '')}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="content-accordion-hint">Önce bir Excel dosyası seçin.</p>
          )}
        </div>

        <div className="modal-footer icerik-excel-import__footer">
          <div className="icerik-excel-import__footer-start">
            {contentType === 'parca' && allRows.length > 0 ? (
              <button
                type="button"
                className="secondary-btn"
                onClick={applyParcaDefaultMappings}
                disabled={importing}
                title="Veri 2. satırdan; parça kodu A, ad B, birim E; brüt fiyat 0; değişim parça garantisi hayır"
              >
                Varsayılan
              </button>
            ) : null}
          </div>
          <div className="icerik-excel-import__footer-actions">
            <button type="button" className="secondary-btn" onClick={onClose} disabled={importing}>
              Kapat
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={runImport}
              disabled={
                importing ||
                !allRows.length ||
                parsedDataStartRow === null ||
                parsedDataStartRow > allRows.length
              }
            >
              {importing ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Aktarılıyor…
                </>
              ) : (
                'İçe aktar'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>

    {mappingEdit ? (
      <div
        className="modal-overlay icerik-excel-import__sub-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="icerik-excel-submap-title"
        onClick={() => setMappingEdit(null)}
      >
        <div
          className="modal icerik-excel-import__sub-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3 id="icerik-excel-submap-title">{mappingEdit.label}</h3>
            <button type="button" className="modal-close-btn" onClick={() => setMappingEdit(null)} aria-label="Kapat">
              ×
            </button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="icerik-excel-sub-col">Excel sütun harfi</label>
              <input
                id="icerik-excel-sub-col"
                type="text"
                className="form-control"
                placeholder="örn. B veya AA"
                value={mappingEdit.column}
                onChange={(e) => setMappingEdit((prev) => (prev ? { ...prev, column: e.target.value } : prev))}
                autoComplete="off"
              />
            </div>
            {mappingEdit.fieldKind === 'bool' ? (
              <>
                <p className="icerik-excel-import__field-hint">
                  Sütundan Evet veya Hayır okunur (büyük/küçük harf duyarsız). Hücre boşsa aşağıdaki varsayılan kullanılır.
                </p>
                <div className="icerik-excel-import__bool-defaults">
                  <span className="icerik-excel-import__bool-defaults-label">Varsayılan (sütun yok veya hücre boş)</span>
                  <label className="icerik-excel-import__check-row">
                    <input
                      type="checkbox"
                      checked={mappingEdit.default === 'true'}
                      onChange={(e) => {
                        setMappingEdit((prev) => {
                          if (!prev) return prev;
                          if (e.target.checked) return { ...prev, default: 'true' };
                          return { ...prev, default: prev.default === 'true' ? '' : prev.default };
                        });
                      }}
                    />
                    <span>Evet (true)</span>
                  </label>
                  <label className="icerik-excel-import__check-row">
                    <input
                      type="checkbox"
                      checked={mappingEdit.default === 'false'}
                      onChange={(e) => {
                        setMappingEdit((prev) => {
                          if (!prev) return prev;
                          if (e.target.checked) return { ...prev, default: 'false' };
                          return { ...prev, default: prev.default === 'false' ? '' : prev.default };
                        });
                      }}
                    />
                    <span>Hayır (false)</span>
                  </label>
                </div>
              </>
            ) : mappingEdit.fieldKind === 'birim' ? (
              <>
                <p className="icerik-excel-import__field-hint">
                  Sütunda <strong>birim adı</strong> (yaygın yazım varyantları da eşlenir), <strong>kod</strong> veya{' '}
                  <strong>birim ID</strong> olabilir. Hücre boşsa veya sütun yoksa, aşağıdaki listede{' '}
                  <strong>sırada ilk</strong> gelen birim kullanılır; listede kayıt yoksa <strong>Birim bulunamadı</strong>{' '}
                  (içe aktarmada birim atanmaz).
                </p>
                <div className="form-group">
                  <label htmlFor="icerik-excel-sub-birim-def">Sütun boşsa kullanılacak birim (liste sırası)</label>
                  {birimler.length === 0 ? (
                    <select id="icerik-excel-sub-birim-def" className="form-control" disabled value="">
                      <option>Birim bulunamadı</option>
                    </select>
                  ) : (
                    <select
                      id="icerik-excel-sub-birim-def"
                      className="form-control"
                      value={effectiveBirimDefaultId(mappingEdit.default)}
                      onChange={(e) =>
                        setMappingEdit((prev) => (prev ? { ...prev, default: e.target.value } : prev))
                      }
                    >
                      {birimler.map((b) => (
                        <option key={String(b.id || b._id)} value={String(b.id || b._id)}>
                          {b.kod} — {b.ad}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </>
            ) : mappingEdit.fieldKind === 'price' ? (
              <div className="form-group">
                <label htmlFor="icerik-excel-sub-def">Varsayılan brüt fiyat (opsiyonel)</label>
                <input
                  id="icerik-excel-sub-def"
                  type="text"
                  className="form-control"
                  placeholder="örn. 199,90 — sütun boşsa kullanılır"
                  value={mappingEdit.default}
                  onChange={(e) => setMappingEdit((prev) => (prev ? { ...prev, default: e.target.value } : prev))}
                  autoComplete="off"
                />
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="icerik-excel-sub-def">Varsayılan değer (opsiyonel)</label>
                <input
                  id="icerik-excel-sub-def"
                  type="text"
                  className="form-control"
                  placeholder="Sütun boşsa kullanılır"
                  value={mappingEdit.default}
                  onChange={(e) => setMappingEdit((prev) => (prev ? { ...prev, default: e.target.value } : prev))}
                  autoComplete="off"
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="secondary-btn" onClick={() => setMappingEdit(null)}>
              İptal
            </button>
            <button type="button" className="primary-btn" onClick={saveMappingEdit}>
              Kaydet
            </button>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
};

export default IcerikExcelImportModal;
